import React, { useState } from 'react';
import { formatCurrency } from '../../shared/givingHelpers';

const CHANNELS = ['mail', 'email', 'online', 'phone', 'in_person', 'event', 'social', 'text'];
const channelLabel = (ch) => ch.replace('_', '-').replace(/\b\w/g, c => c.toUpperCase());

const AppealSection = ({ campaign, team }) => {
  const [showAppealForm, setShowAppealForm] = useState(false);
  const [appealName, setAppealName] = useState('');
  const [appealCode, setAppealCode] = useState('');
  const [appealChannel, setAppealChannel] = useState('');
  const [appealDateSent, setAppealDateSent] = useState('');
  const [appealCostDollars, setAppealCostDollars] = useState('');
  const [appealNumberSolicited, setAppealNumberSolicited] = useState('');
  const [appealSubmitting, setAppealSubmitting] = useState(false);
  const [appealErrors, setAppealErrors] = useState([]);

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  const handleAppealSubmit = (e) => {
    e.preventDefault();
    if (!appealName.trim() || !appealCode.trim() || !appealChannel || appealSubmitting) return;

    setAppealSubmitting(true);
    setAppealErrors([]);

    fetch(`/teams/${team.id}/giving/${campaign.id}/appeals.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({
        appeal: {
          name: appealName,
          appeal_code: appealCode,
          channel: appealChannel,
          date_sent: appealDateSent || null,
          cost_cents: appealCostDollars ? Math.round(parseFloat(appealCostDollars) * 100) : 0,
          number_solicited: appealNumberSolicited ? parseInt(appealNumberSolicited, 10) : 0,
        },
      }),
    })
      .then((response) => {
        if (response.ok) {
          window.location.reload();
        } else {
          return response.json().then((data) => {
            setAppealErrors(data.errors || ['Something went wrong']);
            setAppealSubmitting(false);
          });
        }
      })
      .catch(() => {
        setAppealErrors(['Network error. Please try again.']);
        setAppealSubmitting(false);
      });
  };

  return (
    <div className="gc-section">
      <div className="gc-section-row">
        <div className="gc-section-info">
          <h3 className="gc-section-title">Appeals</h3>
          <p className="gc-section-desc">Outreach efforts driving gifts to this campaign.</p>
        </div>
        <div className="gc-section-fields">
          <div className="gcd-appeals-actions">
            <button
              className="btn btn-sm btn-setlist-secondary"
              onClick={() => setShowAppealForm(!showAppealForm)}
            >
              {showAppealForm ? 'Cancel' : 'Add Appeal'}
            </button>
          </div>

          {showAppealForm && (
            <div className="gcd-appeal-form">
              {appealErrors.length > 0 && (
                <div className="gcd-alert-error">
                  {appealErrors.map((err, i) => <div key={i}>{err}</div>)}
                </div>
              )}
              <form onSubmit={handleAppealSubmit}>
                <div className="gcd-form-grid">
                  <div className="gcd-form-group">
                    <label>Name *</label>
                    <input type="text" value={appealName} onChange={(e) => setAppealName(e.target.value)} required />
                  </div>
                  <div className="gcd-form-group">
                    <label>Appeal Code *</label>
                    <input type="text" value={appealCode} onChange={(e) => setAppealCode(e.target.value)} required />
                  </div>
                  <div className="gcd-form-group">
                    <label>Channel *</label>
                    <select value={appealChannel} onChange={(e) => setAppealChannel(e.target.value)} required>
                      <option value="">Select...</option>
                      {CHANNELS.map(ch => <option key={ch} value={ch}>{channelLabel(ch)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="gcd-form-grid">
                  <div className="gcd-form-group">
                    <label>Date Sent</label>
                    <input type="date" value={appealDateSent} onChange={(e) => setAppealDateSent(e.target.value)} />
                  </div>
                  <div className="gcd-form-group">
                    <label>Cost ($)</label>
                    <input type="number" value={appealCostDollars} onChange={(e) => setAppealCostDollars(e.target.value)} min="0" step="0.01" />
                  </div>
                  <div className="gcd-form-group">
                    <label># Solicited</label>
                    <input type="number" value={appealNumberSolicited} onChange={(e) => setAppealNumberSolicited(e.target.value)} min="0" />
                  </div>
                </div>
                <button type="submit" className="btn btn-sm btn-setlist-primary" disabled={!appealName.trim() || !appealCode.trim() || !appealChannel || appealSubmitting}>
                  {appealSubmitting ? 'Saving...' : 'Create Appeal'}
                </button>
              </form>
            </div>
          )}

          {campaign.appeals && campaign.appeals.length > 0 && (
            <div className="gcd-table-wrap">
              <table className="gcd-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Channel</th>
                    <th>Responses</th>
                    <th>Raised</th>
                    <th>Response Rate</th>
                    <th>Avg Gift</th>
                    <th>ROI</th>
                  </tr>
                </thead>
                <tbody>
                  {campaign.appeals.map(appeal => (
                    <tr key={appeal.id}>
                      <td style={{ fontWeight: 600, color: 'var(--sl-text-primary)' }}>{appeal.name}</td>
                      <td>{channelLabel(appeal.channel)}</td>
                      <td>{appeal.response_count}</td>
                      <td>{formatCurrency(appeal.total_raised_cents)}</td>
                      <td>{appeal.response_rate}%</td>
                      <td>{formatCurrency(appeal.average_gift_cents)}</td>
                      <td>{appeal.roi !== null ? `${appeal.roi}x` : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(!campaign.appeals || campaign.appeals.length === 0) && !showAppealForm && (
            <p className="gcd-empty">No appeals yet. Click "Add Appeal" to create one.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppealSection;
